import { Group, Menu } from '@mantine/core';
import { trackEvent } from '@repo/common/analytics';
import { KeyboardShortcut } from '@repo/common/components/KeyboardShortcut/KeyboardShortcut';
import { useNavigate } from '@repo/common/hooks/useNavigate';
import { Icon, IconButton, Text } from '@repo/foundations';
import { MOD_KEY } from '@repo/secoda-editor/utils/keyboard';
import { observer } from 'mobx-react-lite';
import { useCallback } from 'react';
import { useAuthUser, useDeleteAllAIEmbedded } from '../../../api';
import { openDeleteConfirmModal } from '../../ModalManager';
import { useAIAssistantContext } from '../hooks';
import { aiSidebarSessionStore } from './sessionStore';

export const AIAssistantSidebarHeader = observer(() => {
	const store = useAIAssistantContext();
	const { user, workspace } = useAuthUser();

	const navigate = useNavigate();

	const handleNewChat = () => {
		store.setId('new');
		store.setIsHistoryOpen(false);
		aiSidebarSessionStore.clearSessionId();
	};

	const handleViewHistory = () => {
		store.setIsHistoryOpen(true);
		aiSidebarSessionStore.clearSessionId();
	};

	const handleOpenPage = () => {
		navigate(`/ai/${store.id}`);
	};

	const title = store.isHistoryOpen ? 'Chat history' : 'Secoda AI';

	const showNewChatButton =
		(!!store.id && store.id !== 'new') || store.isHistoryOpen;

	const { mutate: deleteAll } = useDeleteAllAIEmbedded();

	const handleDeleteAll = useCallback(() => {
		openDeleteConfirmModal({
			title: 'Are you sure you want to clear all conversations?',
			onConfirm: () => {
				trackEvent(
					'ai/history/delete_all',
					{
						mode: store.mode,
					},
					user,
					workspace
				);
				deleteAll();
			},
			confirmLabel: 'Clear conversations',
		});
	}, [deleteAll, user, workspace, store.mode]);

	return (
		<Group position="apart" px="xs">
			<Text size="sm" fw={600}>
				{title}
			</Text>
			<Menu keepMounted={false}>
				<Menu.Target>
					<IconButton iconName="dots" variant="tertiary" />
				</Menu.Target>
				<Menu.Dropdown>
					{showNewChatButton && (
						<Menu.Item
							icon={<Icon name="edit" />}
							onClick={handleNewChat}
							rightSection={<KeyboardShortcut keys={[MOD_KEY, 'Shift', 'O']} />}
						>
							New chat
						</Menu.Item>
					)}
					{!store.isHistoryOpen && (
						<Menu.Item
							icon={<Icon name="history" />}
							onClick={handleViewHistory}
						>
							View history
						</Menu.Item>
					)}
					{store.isHistoryOpen && (
						<Menu.Item icon={<Icon name="trash" />} onClick={handleDeleteAll}>
							Delete all chats...
						</Menu.Item>
					)}
					<Menu.Divider />
					<Menu.Item
						icon={<Icon name="circleChevronRight" />}
						onClick={handleOpenPage}
					>
						Open in Secoda AI page
					</Menu.Item>
				</Menu.Dropdown>
			</Menu>
		</Group>
	);
});
